import type { Sprint, TicketListItem } from '../../api/types';
import { parseCalendarDate } from '../../lib/format';

/**
 * 🔴 THE SHAPE OF THE SPRINT BACKLOG, AND THE ONE ORDERING RULE THAT IS NOT NEGOTIABLE.
 *
 * Or's request, verbatim: *"Backlog with separate sprints and last one will be unassign sprint
 * grouping with collapse of each section"*. So: one section per sprint, in the SERVER's order, and
 * the tickets with no sprint in a final section — LAST, always, even when there are no sprints at
 * all. ⭐ That is not cosmetic. A backlog is read top-down as "what we are doing now → what we will
 * do next → what is merely captured"; putting the unplanned pile anywhere but the bottom inverts the
 * meaning of the whole page.
 *
 * ⛔ THE SPRINT ORDER IS THE API'S AND IS NOT RECOMPUTED HERE. `listSprints` orders active → planned
 * (soonest first) → closed (most recent first), and it can do that because db/001 enforces at most
 * one active sprint per project. A client-side re-sort would need that knowledge and would drift
 * from it the moment the rule changed. Same reasoning as `buildBacklogRows`, which never re-sorts
 * the rows the server sent.
 */

/** The unassigned section's key. ⭐ Sprint ids are numeric strings, so this can never collide. */
export const UNASSIGNED_SECTION_KEY = 'backlog';

export type SectionKey = string;

export interface BacklogSection {
  key: SectionKey;
  /** `APFS v1.0.33`, or `Backlog` for the unassigned pile. */
  name: string;
  /** `null` for the unassigned section — it is not a sprint and has no dates or state. */
  sprint: Sprint | null;
}

/**
 * Sections in draw order: every sprint the project has, then the unassigned pile.
 *
 * ⚠️ CLOSED SPRINTS ARE INCLUDED HERE. A ticket still pointing at a closed sprint has to be somewhere,
 * and "nowhere" is the one answer this codebase does not allow — the same rule that keeps a detached
 * sub-task on screen rather than dropping it between pages.
 *
 * ⭐ PH-52 withholds them from the DRAW, one step later, in `hideClosedSections`. Deliberately not
 * here: this function is the complete map of where a ticket can be, and the moment it starts omitting
 * a sprint, "which section holds APFS-41?" gets a wrong answer everywhere the map is consulted.
 */
export function buildSections(sprints: readonly Sprint[]): BacklogSection[] {
  return [
    ...sprints.map((sprint) => ({ key: sprint.id, name: sprint.name, sprint })),
    { key: UNASSIGNED_SECTION_KEY, name: 'Backlog', sprint: null },
  ];
}

// ── PH-52 — closed sprints are withheld from the DRAW, and only from the draw ──────────────────────

/**
 * What is left after the closed sprints are withheld, plus what the reader must be told about it.
 *
 * ⭐ `hideable` is the count the CONTROL is labelled with, and it is the number of sections the
 *   toggle can actually move — not "how many closed sprints exist". The difference is the pinned case
 *   below: with a closed sprint named by the sprint filter, one closed section is on screen and the
 *   toggle cannot remove it, so counting it would label the button with a promise it will not keep.
 *   ⛔ `hideable === 0` therefore means DRAW NO CONTROL: a button whose only possible outcome is
 *   "nothing visibly happened" is the inert-feature failure this repo has shipped before.
 */
export interface ClosedSprintView {
  /** The sections to draw, in the same order they arrived. */
  sections: BacklogSection[];
  /**
   * Where the closed block starts in `sections` — the index the reveal control belongs at, so it
   * stays anchored at the boundary and the revealed sections appear directly BELOW the finger that
   * tapped it. `null` when the project has no closed sprint at all.
   */
  boundary: number | null;
  /** How many sections the toggle governs. See above: this is what the control is labelled with. */
  hideable: number;
}

/**
 * 🔴 PH-52 — Or, 2026-08-17: *"not show closed sprint in backlog tabs or it will so much"*, and then
 * *"the filtered in backlog will help in long run trust me"*.
 *
 * He is describing a board that gets worse every day it is used. Project PH had SEVEN sprints after
 * two days — one active, four planned, two closed — and every one of them draws a two-line sticky
 * header. At 390px that is most of the screen before a single ticket is visible, and the closed pile
 * is the only part of it that grows without bound.
 *
 * ⛔⛔ THIS IS A RENDER FILTER AND IT MUST STAY ONE. The same `Sprint[]` from
 *   `GET /projects/:id/sprints` also feeds the Complete-sprint dialog's carry-over picker — whose
 *   whole job is to offer sprints by the API's rule (`carryOverTargets`: everything that is not the
 *   closing sprint and not closed) — and the versions views. Filtering closed sprints out of the
 *   FETCHED list would silently narrow all of them. ⇒ The list stays whole; this takes sections.
 * ⚠️ And the request keeps asking for closed sprints too: `ticket_count`, `story_points_total` and
 *   PH-37's progress fields are computed server-side in ONE aggregate precisely so a header costs no
 *   extra request. A revealed section must not have to go and fetch what it was already sent.
 *
 * ⭐⭐ HIDDEN, NOT MERELY COLLAPSED — and the most recent closed sprint gets NO exemption. Argued,
 *   because the opposite is tempting:
 *     · A collapsed closed section already shows no ticket. So "what did we just ship" is NOT on
 *       screen today either — it is one tap away today and one tap away after this change. The
 *       exemption would preserve a REMINDER that the sprint exists, not the answer to the question.
 *     · An exemption makes the rule "closed sprints are hidden, except one", which a reader cannot
 *       predict, and it produces a section that VANISHES ON ITS OWN the day the next sprint closes —
 *       a view changing as a side effect of an unrelated write, with no control touched.
 *     · The count never leaves the screen. The control says `Show 2 closed sprints`, so "we have
 *       shipped two" survives; only the two headers go.
 *
 * ⭐ `keep` — THE SECTION THE READER ASKED FOR BY NAME. The sprint filter can name a closed sprint
 *   (the picker lists them, and it should — you filter to a closed sprint precisely to review it).
 *   Hiding it would answer a direct question with a blank page, and a blank page under an explicit
 *   filter is indistinguishable from "this sprint is empty". ⛔ An explicit request beats a default,
 *   always.
 */
export function hideClosedSections(
  sections: readonly BacklogSection[],
  options: { show: boolean; keep?: SectionKey | null },
): ClosedSprintView {
  const kept: BacklogSection[] = [];
  let boundary: number | null = null;
  let hideable = 0;

  for (const section of sections) {
    if (section.sprint?.state !== 'closed') {
      kept.push(section);
      continue;
    }

    // The first closed section fixes where the control goes — whether or not it survives the filter.
    if (boundary === null) boundary = kept.length;

    if (section.key === options.keep) {
      kept.push(section);
      continue;
    }

    hideable += 1;
    if (options.show) kept.push(section);
  }

  return { sections: kept, boundary, hideable };
}

/**
 * Which sections start open.
 *
 * 🔴 THE DEFAULT IS THE DECISION THAT DECIDES WHETHER THIS PAGE IS USEFUL. Everything-collapsed shows
 * a reader nothing and makes them work to see anything; everything-open on a project with eleven
 * closed sprints is a 500-row wall. The rule:
 *
 *   1. The ACTIVE sprint is open — it is what the team is working on, and db/001 guarantees at most
 *      one, so this cannot open five sections by accident.
 *   2. If there is NO active sprint, the first PLANNED one is open instead — "the sprint you will
 *      work in next" is the closest thing to an answer when nothing is running.
 *   3. The unassigned Backlog section is ALWAYS open. It is the section Or named, it is the one that
 *      exists in every project, and on a project with no sprints at all it is the entire page.
 *   4. Everything else — planned-but-not-next, and every closed sprint — starts collapsed, with its
 *      count and points still on the header, which is the part you scan for.
 *
 * ⭐ Consequence worth stating: the page can never open with nothing visible, because (3) has no
 * precondition.
 */
export function defaultOpenKeys(sprints: readonly Sprint[]): Set<SectionKey> {
  const open = new Set<SectionKey>([UNASSIGNED_SECTION_KEY]);

  const active = sprints.find((sprint) => sprint.state === 'active');
  if (active) {
    open.add(active.id);
    return open;
  }

  const nextPlanned = sprints.find((sprint) => sprint.state === 'planned');
  if (nextPlanned) open.add(nextPlanned.id);
  return open;
}

/**
 * The open/closed answer for one section: the default, unless the URL overrides it.
 *
 * ⭐ OVERRIDES RATHER THAN A FULL SNAPSHOT. `/backlog?project=1` means "the sensible default", not
 * "whatever was open when this link was written" — so the link keeps working when a sprint closes
 * and a new one starts, which is the thing that actually happens between sending a link and opening
 * it. Only a section somebody deliberately toggled is named in the URL.
 */
export function isSectionOpen(
  key: SectionKey,
  defaults: ReadonlySet<SectionKey>,
  overrides: { opened: ReadonlySet<SectionKey>; collapsed: ReadonlySet<SectionKey> },
): boolean {
  if (overrides.collapsed.has(key)) return false;
  if (overrides.opened.has(key)) return true;
  return defaults.has(key);
}

/**
 * The story-point sum for a section's header.
 *
 * ⚠️ NUMERIC(5,2) arrives as a STRING (`"10.00"`) — see `formatPoints`. Anything unparseable is
 * skipped rather than counted as zero, and a ticket with no estimate contributes nothing, which is
 * what "no estimate" means. ⛔ The caller must mark the result as a floor when it summed fewer items
 * than the sprint holds; this function cannot know that.
 */
export function sumStoryPoints(items: readonly TicketListItem[]): number {
  let total = 0;
  for (const item of items) {
    const value = Number(item.story_points);
    if (item.story_points === null || item.story_points === '' || !Number.isFinite(value)) continue;
    total += value;
  }
  // NUMERIC(5,2) sums accumulate float noise (0.1 + 0.2); two decimals is the column's own precision.
  return Math.round(total * 100) / 100;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

interface Ymd {
  year: string;
  month: string;
  day: string;
}

/**
 * ⛔ `YYYY-MM-DD` IS SPLIT, NEVER `new Date()`-ED. A Postgres DATE is cast to text server-side exactly
 * so it is not a timestamp; feeding it to `new Date()` parses it as UTC midnight and
 * `toLocaleDateString` in Asia/Bangkok then prints it correctly — but `new Date('2026-07-14')` in a
 * browser west of UTC prints the 13th. A sprint that starts a day early on half the team's screens is
 * the quiet kind of wrong, so no Date object is constructed at all.
 *
 * ⭐ The SPLIT itself now lives in `lib/format.ts` as `parseCalendarDate` — the same one
 * `formatCalendarDate` uses — so there is ONE answer in this app to "is this a plain calendar date".
 * Only the English month name and the display shape stay here, because only this range format wants
 * them. Behaviour is unchanged, including accepting a range-valid-but-impossible `2026-02-30`.
 */
function splitYmd(value: string): Ymd | null {
  const parts = parseCalendarDate(value);
  if (!parts) return null;
  return {
    year: String(parts.year).padStart(4, '0'),
    month: MONTHS[parts.month - 1] as string,
    day: String(parts.day),
  };
}

/**
 * `14 Jul – 14 Aug`, as on Or's board — and `14 Dec 2026 – 3 Jan 2027` when the range crosses a year,
 * because "14 Dec – 3 Jan" is genuinely ambiguous about which January.
 *
 * ⚠️ Returns the raw strings joined if either date is not a plain `YYYY-MM-DD`. ⛔ Not an empty
 * string: showing nothing where a date belongs reads as "this sprint has no dates", and the column
 * is NOT NULL in db/001, so that would be a claim the data contradicts.
 */
export function formatSprintRange(startsOn: string, endsOn: string): string {
  const from = splitYmd(startsOn);
  const to = splitYmd(endsOn);
  if (!from || !to) return `${startsOn} – ${endsOn}`;

  if (from.year === to.year) return `${from.day} ${from.month} – ${to.day} ${to.month}`;
  return `${from.day} ${from.month} ${from.year} – ${to.day} ${to.month} ${to.year}`;
}

export const SPRINT_STATE_LABEL: Record<Sprint['state'], string> = {
  active: 'Active',
  planned: 'Planned',
  closed: 'Closed',
};

// ── Section totals ────────────────────────────────────────────────────────────────────────────────

/** One sprint section's rows, as returned by `GET /tickets?sprintId=`. */
export interface SectionPage {
  items: TicketListItem[];
  /** The server's count under the CURRENT filters. Exact. */
  total: number;
}

/**
 * How to read a number on a section header. ⭐ THREE STATES, BECAUSE THERE ARE THREE.
 *
 *   exact        — this is the number, under exactly the filters on screen.
 *   floor        — at least this. Renders `26+`.
 *   whole-sprint — the SPRINT's own number, which is not the filtered view's. Renders with a note.
 *
 * ⛔ Collapsing these into one boolean is how "26" ends up over a section that would show three rows
 * if you opened it, with nothing on screen to say why.
 */
export type CountScope = 'exact' | 'floor' | 'whole-sprint';

export interface SectionTotals {
  count: number | null;
  countScope: CountScope;
  points: number | null;
  pointsScope: CountScope;
}

export const UNKNOWN_TOTALS: SectionTotals = {
  count: null,
  countScope: 'exact',
  points: null,
  pointsScope: 'exact',
};

/**
 * A sprint section's header numbers.
 *
 * ⭐⭐ THE COLLAPSED CASE COSTS NO REQUEST. `GET /projects/:id/sprints` carries `ticket_count` and
 * `story_points_total` per sprint, computed in ONE lateral server-side — so a collapsed section can
 * show real numbers without asking for its rows. That is the whole reason this screen does not fire
 * one request per sprint on load.
 *
 * ⚠️ AND THE CATCH, STATED RATHER THAN IGNORED: those two numbers count the WHOLE sprint. They know
 * nothing about the status or assignee filter on screen. So while a filter is active a collapsed
 * section reports them as `whole-sprint`, and the header says so. ⛔ Printing them as though they
 * were the filtered count is the "confident wrong number" this codebase refuses everywhere else.
 *
 * ⭐ An OPEN section has asked its own filtered question and answers from that instead — exact.
 */
export function sprintSectionTotals(
  sprint: Sprint,
  page: SectionPage | null,
  filtered: boolean,
): SectionTotals {
  if (page) {
    return {
      count: page.total,
      countScope: 'exact',
      points: sumStoryPoints(page.items),
      // The row request is capped at MAX_PAGE_SIZE, so a sprint of more than 100 has an exact count
      // above a short list — and therefore a point sum that is only a floor.
      pointsScope: page.items.length < page.total ? 'floor' : 'exact',
    };
  }

  const scope: CountScope = filtered ? 'whole-sprint' : 'exact';
  return {
    count: sprint.ticket_count,
    countScope: scope,
    points: sprint.story_points_total,
    pointsScope: scope,
  };
}

/**
 * The unassigned section's header numbers.
 *
 * ✅ The COUNT is now the server's own, via `noSprint=true` — exact, not the floor it used to be when
 *   this section was assembled by scanning the project's pages. ⛔ The `+` had to go with the scan;
 *   an exact number wearing a floor's suffix is the same dishonesty pointing the other way.
 *
 * ⚠️ THE POINT SUM IS THE ONE THAT CAN STILL BE A FLOOR, and for a reason that has nothing to do with
 *   the old workaround: points are summed over the ROWS WE HOLD, and the API caps a page at 100. So a
 *   section with 140 unassigned tickets has an exact count of 140 above a sum of the first 100.
 *   ⭐ Same split `sprintSectionTotals` already makes for a large sprint — the count comes from the
 *     server, the sum from the rows, and only the sum degrades.
 */
export function unassignedSectionTotals(
  items: readonly TicketListItem[],
  total: number,
  truncated: boolean,
): SectionTotals {
  return {
    count: total,
    countScope: 'exact',
    points: sumStoryPoints(items),
    pointsScope: truncated ? 'floor' : 'exact',
  };
}
